// External tools
import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, reactive, ref, watch } from 'vue'
import { useQueries, useQuery } from '@tanstack/vue-query'
import { cloneDeep } from 'lodash'
import { DateTime } from 'luxon'
import { v1 as uuid } from 'uuid'

// Internal tools
import router from '@/router.js'
import i18n from '@/i18n/SetupI18n'
import { useLocations } from '@/composables/locations.js'
import { useTransitPorts } from '@/composables/transitPorts.js'
import { HTTP_CONTENT_TYPES } from '@/utilities/httpClients'
import { callCpqLite } from '@/utilities/cpqLiteApi'
import { pricebookQueryFactory } from '@/utilities/query-factories/pricebookQueryFactory'
import { searchCompaniesQuery } from '@/utilities/megalithQueries'
import { generatePricebookNotificationOptions } from '@/utilities/priceBook'
import { useAuthStore } from '@/stores/auth.js'
import vueQueryPluginOptions from '@/utilities/vueQueryPluginOptions.js'
import { formatCurrencyAmount } from '@/helpers.js'
// Globals
import {
  ACCOUNT_TYPE_DIRECT_CUSTOMER,
  ACCOUNT_TYPE_MANAGED_ACCOUNT,
  ACCOUNT_TYPE_PARTNER,
  ACCOUNT_TYPE_DISCOUNT_REASONS,
  PRODUCT_TYPE_MEGAPORT,
  PRODUCT_TYPE_MCR2,
  PRODUCT_TYPE_MVE,
  PRODUCT_TYPE_LAG,
  PRODUCT_TYPE_VXC,
  PRODUCT_TYPE_IX,
  QUOTE_STATUS_REGISTERED,
  QUOTE_STATUS_SAVED,
  MEGAPORT_ID,
  MEGAPORT_LAB_ID,
  TARGET_TYPES,
} from '@/Globals.js'

export const useConfiguredServicesStore = defineStore('configuredServices', () => {
  const quoteIsDirty = ref(false)

  const authStore = useAuthStore()
  const { hasFeatureFlag } = authStore

  const quoteConfig = reactive({
    // User-Entered Data
    targetType: '', // The type of customer/account the quote is for (EXISTING - NEW - SELF)
    name: '', // Quote name
    managedCompany: '', // Name of the managed company
    companyUid: '', // The UUID of the company the quote is for
    includeAccountTypeDiscounts: false, // Whether to include type discounts (WHOLESALE - PARTNER - PARTNER_MANAGED) in the quote
    rollUpDiscounts: true, // Whether to hide discount line items in the UI and PDF
    // API-Retrieved Data
    id: '', // 32-bit ID string retrieved from the API after the quote is saved
    ownerCompanyId: '', // The ID of the company the quote was created by
    originalOwnerCompanyId: null, // The ID of the company the quote was originally created by
    partner: '', // Company name of the logged in user at the time the quote was created
    createdBy: '', // Name of the logged in user at the time the quote was created
    createDate: '', // ISO 8601 date string retrieved from the API after the quote is saved
    projectState: '', // The state of the quote (either SAVED or REGISTERED) retrieved from the API after the quote is saved
    deal: {
      id: '', // Deal ID from Zift once registered
    },
  })

  const serviceQuotes = ref([])

  /**
   * The formatted creation date of the quote to be displayed in the UI.
   */
  const formattedCreateDate = computed(() => {
    let date = DateTime.utc()
    const dateFormat = 'LLLL d y HH:mm'

    if (quoteConfig.createDate) {
      date = DateTime.fromISO(quoteConfig.createDate)

      if (date.isValid) return date.toFormat(dateFormat)
    }

    return date.toFormat(dateFormat)
  })

  const isForeignQuote = computed(() => quoteConfig.ownerCompanyId !== authStore.userData.companyId)
  const ownerIsMegaportAdminOrLab = computed(() => [MEGAPORT_ID, MEGAPORT_LAB_ID].includes(quoteConfig.ownerCompanyId))
  const isForeignQuoteNotOwnedByMegaport = computed(() => isForeignQuote.value && !ownerIsMegaportAdminOrLab.value)

  const isForSelf = computed(() => quoteConfig.targetType === TARGET_TYPES.SELF)
  const isForExistingCompany = computed(() => quoteConfig.targetType === TARGET_TYPES.EXISTING_COMPANY)
  const isForNewCompany = computed(() => quoteConfig.targetType === TARGET_TYPES.NEW_COMPANY)

  const isRegisteredQuote = computed(() => quoteConfig.projectState === QUOTE_STATUS_REGISTERED)

  const { data: existingTargetCompany } = useQuery({
    ...searchCompaniesQuery(() => quoteConfig.companyUid, false),
    select: searchResults => searchResults.find(company => company.companyUid === quoteConfig.companyUid),
    enabled: () => authStore.isMegaportAdminOrLabAccount && isForExistingCompany.value && !!quoteConfig.companyUid,
    meta: {
      vueInstance: window.mpApp,
      notificationOptions: {
        title: i18n.t('company.company-loading-error'),
        message: i18n.t('company.target-company-loading-error-long'),
      },
    },
  })

  const quoteTargetAccountType = computed(() => {
    if (authStore.isPartnerAccount) {
      // If the quote is being created by a partner for themselves, return the PARTNER account type.
      if (isForSelf.value) return ACCOUNT_TYPE_PARTNER
      // If the quote is being created by a partner for an existing or new managed company, return the MANAGED_ACCOUNT type.
      if (isForExistingCompany.value || isForNewCompany.value) return ACCOUNT_TYPE_MANAGED_ACCOUNT
    }

    // If the quote is for the logged-in user, return their account type.
    if (isForSelf.value) return authStore.userData.accountType
    // If the quote is for an existing company, return the account type of that company, which is retrieved from the API.
    if (isForExistingCompany.value) return existingTargetCompany.value?.accountType
    // If the quote is for a new company, the account type is not known yet, so return undefined.
    return undefined
  })

  const canReceiveAccountTypeDiscounts = computed(() => {
    const applicableAccountTypes = [ACCOUNT_TYPE_DIRECT_CUSTOMER, ACCOUNT_TYPE_MANAGED_ACCOUNT, ACCOUNT_TYPE_PARTNER]

    return applicableAccountTypes.includes(quoteTargetAccountType.value)
  })

  /**
   * Saves the quote to the API, either updating an existing quote or creating a new one.
   * It assumes it's a new quote when a quote ID is not present.
   * @param {boolean} [withinQuoteBuilder] - Whether the save was initiated from the Quote Builder
   * @param {boolean} [notifyUser] - Whether to notify the user of a successful save
   */
  const saveQuote = async (withinQuoteBuilder = true, notifyUser = true) => {
    const requestConfig = {
      headers: { 'Content-Type': HTTP_CONTENT_TYPES.JSON },
      data: assembleSavePayload(),
    }

    try {
      // Update an existing quote
      if (quoteConfig.id) {
        requestConfig.method = 'put'
        requestConfig.url = `projects/${quoteConfig.id}`

        await callCpqLite(requestConfig)
      } else {
        // Create a new quote
        requestConfig.method = 'post'
        requestConfig.url = 'projects'

        const { id, deal } = await callCpqLite(requestConfig)

        quoteConfig.id = id
        quoteConfig.deal = deal

        // Update the path's params
        if (withinQuoteBuilder) {
          router.replace({ params: { quoteId: id } })
        } else {
          // Navigate to Quote Builder
          router.push(`/quote-builder/${id}`, () => {})
        }
      }

      quoteConfig.projectState = QUOTE_STATUS_SAVED
      quoteIsDirty.value = false

      // Invalidate the quote query to ensure the latest data is fetched.
      // This prevents the user from seeing stale data when navigating back to their recently saved quote.
      vueQueryPluginOptions.queryClient.invalidateQueries({ queryKey: ['quote', quoteConfig.id] })

      if (notifyUser) {
        // Notify the user of a successful save
        window.mpApp.$message.success({ message: i18n.t('quote.saved') })
      }
    } catch (error) {
      // Notify the user of an unsuccessful save
      window.mpApp.$notify.error({
        title: i18n.t('quote.saving-error'),
        message: error.message || i18n.t('quote.saving-error-long', { quoteId: quoteConfig.id }),
      })
    }
  }

  /**
   * Converts an internal representation of a quote
   * to the format required by the API for the quote to be saved.
   */
  const assembleSavePayload = () => {
    const { targetType, name, partner, managedCompany, companyUid, includeAccountTypeDiscounts, rollUpDiscounts } =
      quoteConfig

    // Assemble the service quotes in the required format.
    const services = cloneDeep(serviceQuotes.value)

    // Since the includeAccountTypeDiscounts and rollUpDiscounts flags affect the displayed pricing details but not the actual underlying pricing,
    // we need to ensure that the displayed pricing details are included in the payload so that the BE can generate the CSV with data that matches the UI.
    const displayedPricingDetailsById = perCurrencyQuoteSummaries.value
      .flatMap(currencyGroup => currencyGroup.serviceQuotes)
      .reduce((acc, service) => {
        // Add the service's pricing data to the accumulator
        acc[service.id] = {
          displayedMonthlyRate: service.price.monthlyNetCharge,
          displayedDiscounts: service.price.discountsToDisplay,
        }
        // Add the service's connections' pricing data to the accumulator
        for (const connection of service.connectionQuotes) {
          acc[connection.id] = {
            displayedMonthlyRate: connection.price.monthlyNetCharge,
            displayedDiscounts: connection.price.discountsToDisplay,
          }
        }

        return acc
      }, {})

    for (const service of services) {
      // Send the displayed pricing details for the service. This is used in the BE when generating the CSV data.
      service.price = {
        ...service.price,
        ...displayedPricingDetailsById[service.id],
      }

      // These IDs are internal and should not be sent to the API.
      delete service.id

      for (const connection of service.connectionQuotes) {
        // Send the displayed pricing details for the connection. This is used in the BE when generating the CSV data.
        connection.price = {
          ...connection.price,
          ...displayedPricingDetailsById[connection.id],
        }

        // These IDs are internal and should not be sent to the API.
        delete connection.id
      }
    }

    return {
      targetType,
      name,
      partner,
      managedCompany,
      companyUid,
      includeAccountTypeDiscounts,
      rollUpDiscounts,
      serviceQuotes: services,
    }
  }

  /**
   * Re-establishes a clean slate for the quote configurations.
   */
  const clearConfigurations = () => {
    serviceQuotes.value = []

    quoteConfig.targetType = ''
    quoteConfig.name = ''
    quoteConfig.managedCompany = ''
    quoteConfig.companyUid = ''
    quoteConfig.includeAccountTypeDiscounts = false
    quoteConfig.rollUpDiscounts = true
    quoteConfig.id = ''
    quoteConfig.ownerCompanyId = ''
    quoteConfig.originalOwnerCompanyId = ''
    quoteConfig.partner = ''
    quoteConfig.createdBy = ''
    quoteConfig.createDate = ''
    quoteConfig.projectState = ''
    quoteConfig.deal = { id: '' }

    quoteIsDirty.value = false
  }

  /**
   * Populates the quote configuration with data retrieved from the API.
   * @param {Object} payload The quote data retrieved from the API
   */
  const populateQuoteFromApi = payload => {
    // Populate the quote configuration
    for (const key of Object.keys(quoteConfig)) {
      quoteConfig[key] = payload[key]
    }

    // Before we set the array of service quotes in the store,
    // we want to enhance the services and connections with IDs so price accumulation is possible.
    const { serviceQuotes: clonedServiceQuotes } = cloneDeep(payload)

    for (const serviceQuote of clonedServiceQuotes) {
      serviceQuote.id = uuid()

      for (const connectionQuote of serviceQuote.connectionQuotes) {
        connectionQuote.id = uuid()
      }
    }

    // Set the service quotes in the store
    serviceQuotes.value = clonedServiceQuotes

    quoteIsDirty.value = false
  }

  /**
   * Registers a deal against the current quote.
   * @param {string} dealId The deal ID to register against the quote
   */
  const registerDeal = async dealId => {
    const requestConfig = {
      method: 'put',
      url: `projects/${quoteConfig.id}/register`,
      headers: { 'Content-Type': HTTP_CONTENT_TYPES.JSON },
      data: { id: dealId },
    }

    await callCpqLite(requestConfig)

    quoteConfig.deal = { id: dealId }
    quoteConfig.projectState = QUOTE_STATUS_REGISTERED

    quoteIsDirty.value = false
  }

  const { enhancedLocations } = useLocations()

  /**
   * The list of configured services, enhanced with additional properties.
   */
  const enhancedServices = computed(() => {
    const services = cloneDeep(serviceQuotes.value)

    for (const service of services) {
      // Calculate the aggregated speed of the service or LAG
      service._aggSpeed =
        service.serviceType === PRODUCT_TYPE_LAG ? service.rateLimit * service.portCount : service.rateLimit

      // Look up the service location
      const location = enhancedLocations.value.find(({ id }) => id === service.location)

      if (location) {
        service._location = location
      }

      // Add the base pricebook query options to the enhanced service for future reference.
      service._basePricebookQueryOptions = pricebookQueryFactory[determineResourceType(service)](
        buildPricebookQueryPayload(service),
        pricebookQueryRequestConfig,
      )

      // Enhance the connections and calculate the allocated speed
      let allocatedSpeed = 0

      for (const connection of service.connectionQuotes) {
        allocatedSpeed += connection.speed

        connection.aEnd = {
          id: service.id,
          location: service.location,
          serviceType: service.serviceType,
        }

        // Add the base pricebook query options to the enhanced connection for future reference.
        connection._basePricebookQueryOptions = pricebookQueryFactory[determineResourceType(connection)](
          buildPricebookQueryPayload(connection),
          pricebookQueryRequestConfig,
        )
      }

      // Calculate the allocated percentage based on the service's aggregated speed
      service._capacity = {
        allocatedSpeed,
        allocatedPercentage: Math.round((allocatedSpeed / service._aggSpeed) * 100),
      }

      // The cloud provider services include a title, which we currently use in the Item Selector.
      service.title = service.name
    }

    return services
  })

  const pricebookQueryRequestConfig = computed(() => {
    // For foreign quotes not owned by Megaport, the X-Call-Context header is not relevant since custom pricing is handled via the `companyId` query parameter.
    if (isForeignQuoteNotOwnedByMegaport.value) return {}

    return { headers: { 'X-Call-Context': quoteConfig.companyUid } }
  })

  const determineResourceType = resource => {
    if (Object.hasOwn(resource, 'serviceType')) {
      switch (resource.serviceType) {
        case PRODUCT_TYPE_MCR2:
          return 'mcr'
        case PRODUCT_TYPE_LAG:
          return 'megaport'
        default:
          return resource.serviceType.toLowerCase()
      }
    } else if (Object.hasOwn(resource, 'connectionType')) {
      return resource.connectionType.toLowerCase()
    }
  }

  const buildPricebookQueryPayload = resource => {
    const queryPayload = {
      currency: resource.currency,
      targetType: quoteConfig.targetType,
    }

    if (Object.hasOwn(resource, 'serviceType')) {
      switch (resource.serviceType) {
        case PRODUCT_TYPE_MEGAPORT:
        case PRODUCT_TYPE_MCR2:
        case PRODUCT_TYPE_LAG:
          queryPayload.locationId = resource.location
          queryPayload.speed = resource.rateLimit
          queryPayload.term = resource.term
          break
        case PRODUCT_TYPE_MVE:
          queryPayload.locationId = resource.location
          queryPayload.vendor = resource.vendor
          queryPayload.size = resource.size
          queryPayload.term = resource.term
          break
      }

      if ([PRODUCT_TYPE_MEGAPORT, PRODUCT_TYPE_LAG].includes(resource.serviceType)) {
        queryPayload.crossConnectRequested = resource.crossConnectRequested || false
      }
    } else if (Object.hasOwn(resource, 'connectionType')) {
      let aEndProductType = resource.aEnd.serviceType
      let connectType = resource.connectType?.split('|')[0]

      switch (resource.connectionType) {
        case PRODUCT_TYPE_VXC:
          if (aEndProductType === PRODUCT_TYPE_LAG) {
            aEndProductType = PRODUCT_TYPE_MEGAPORT
          }

          if (connectType === 'PRIVATE') {
            connectType = 'DEFAULT'
          }

          queryPayload.aLocationId = resource.aEnd.location
          queryPayload.bLocationId = resource.bEndLocation
          queryPayload.speed = resource.speed
          queryPayload.aEndProductType = aEndProductType
          queryPayload.connectType = connectType
          queryPayload.term = resource.term
          break
        case PRODUCT_TYPE_IX:
          queryPayload.ixType = resource.ixType
          queryPayload.portLocationId = resource.aEnd.location
          queryPayload.speed = resource.speed
          break
      }
    }

    // When dealing with a foreign quote not owned by Megaport, include the `companyId` query parameter in the request for custom pricing.
    if (isForeignQuoteNotOwnedByMegaport.value) {
      queryPayload.companyId = quoteConfig.ownerCompanyId
    }

    return queryPayload
  }

  const pricebookQueries = computed(() => {
    const uniqueQueryKeys = new Set()
    const queries = []

    for (const service of enhancedServices.value) {
      const serviceBasePricebookQueryOptions = service._basePricebookQueryOptions

      // Ensure we don't add queries with duplicate query keys as this may cause some data to be shared between queries.
      const serialisedServicePricebookQueryKey = JSON.stringify(serviceBasePricebookQueryOptions.queryKey)

      if (!uniqueQueryKeys.has(serialisedServicePricebookQueryKey)) {
        uniqueQueryKeys.add(serialisedServicePricebookQueryKey)

        queries.push({
          ...serviceBasePricebookQueryOptions,
          // NOTE: In theory, this could lead to an issue where too many toasts are shown at once if many queries fail at once.
          // In practice, this is unlikely to happen. If it does, we can revisit this approach but for now not over-engineering this is the preferred approach.
          meta: {
            vueInstance: window.mpApp,
            notificationOptions: generatePricebookNotificationOptions(service.serviceType),
          },
        })
      }

      for (const connection of service.connectionQuotes) {
        const connectionBasePricebookQueryOptions = connection._basePricebookQueryOptions

        // Ensure we don't add queries with duplicate query keys as this may cause some data to be shared between queries.
        const serialisedConnectionPricebookQueryKey = JSON.stringify(connectionBasePricebookQueryOptions.queryKey)

        if (!uniqueQueryKeys.has(serialisedConnectionPricebookQueryKey)) {
          uniqueQueryKeys.add(serialisedConnectionPricebookQueryKey)

          queries.push({
            ...connectionBasePricebookQueryOptions,
            // NOTE: In theory, this could lead to an issue where too many toasts are shown at once if many queries fail at once.
            // In practice, this is unlikely to happen. If it does, we can revisit this approach but for now not over-engineering this is the preferred approach.
            meta: {
              vueInstance: window.mpApp,
              notificationOptions: generatePricebookNotificationOptions(connection.connectionType),
            },
          })
        }
      }
    }

    return queries
  })

  const pricingResults = useQueries({
    queries: pricebookQueries,
    combine: results => {
      return {
        results,
        isFetching: results.some(result => result.isFetching),
        hasDiscounts: results.some(result => {
          const discounts = result.data?.discounts

          if (!discounts) return false

          return discounts.some(discount => discount.amount > 0)
        }),
        hasAccountTypeDiscounts: results.some(result => {
          const discounts = result.data?.discounts

          if (!discounts) return false

          return discounts.some(discount =>
            Object.values(ACCOUNT_TYPE_DISCOUNT_REASONS).includes(discount.discountReason),
          )
        }),
      }
    },
  })

  watch(
    () => pricingResults.value.isFetching,
    async isFetching => {
      // Once the fetch is complete, update the pricing details for each service and connection.
      if (!isFetching) {
        for (const service of enhancedServices.value) {
          // Get and assign the latest pricing details for the service.
          const serviceQueryKey = service._basePricebookQueryOptions.queryKey
          const newServicePricingDetails = vueQueryPluginOptions.queryClient.getQueryData(serviceQueryKey)

          if (newServicePricingDetails) {
            updateServicePricing(service.id, newServicePricingDetails)
          }

          for (const connection of service.connectionQuotes) {
            // Get and assign the latest pricing details for the connection.
            const connectionQueryKey = connection._basePricebookQueryOptions.queryKey
            const newConnectionPricingDetails = vueQueryPluginOptions.queryClient.getQueryData(connectionQueryKey)

            if (newConnectionPricingDetails) {
              updateConnectionPricing(service.id, connection.id, newConnectionPricingDetails)
            }
          }
        }

        // Save the quote with the updated pricing details.
        await saveQuote(true, false)
      }
    },
  )

  const isIgnoringCustomDiscounts = computed(() => {
    return canReceiveAccountTypeDiscounts.value && !quoteConfig.includeAccountTypeDiscounts
  })

  const getNonAccountTypeDiscounts = discounts => {
    if (!discounts) return []

    return discounts.filter(discount => !Object.values(ACCOUNT_TYPE_DISCOUNT_REASONS).includes(discount.discountReason))
  }

  const getBaseDisplayMonthlyRate = pricingDetails => {
    const baseDisplayMonthlyRate = pricingDetails.monthlyRate

    // Custom discounts apply and the user wants to see pricing without these discounts being applied.
    if (isIgnoringCustomDiscounts.value) {
      // Calculate the base display monthly rate by subtracting the non-custom discounts from the monthly rack rate.
      const nonCustomDiscounts = getNonAccountTypeDiscounts(pricingDetails.discounts)
      const applicableDiscountAmount = nonCustomDiscounts.reduce((sum, discount) => sum + discount.amount, 0)

      return pricingDetails.monthlyRackRate - applicableDiscountAmount
    }

    return baseDisplayMonthlyRate
  }

  const getDiscountsToDisplay = discounts => {
    // If the user wants to roll up the discounts, no discounts are displayed.
    if (quoteConfig.rollUpDiscounts) return []

    // Custom discounts apply but the user has chosen to ignore them.
    if (isIgnoringCustomDiscounts.value) {
      // Return only the non-custom discounts.
      return getNonAccountTypeDiscounts(discounts)
    }

    // Otherwise, return all discounts.
    return discounts || []
  }

  const getMonthlyPriceToDisplay = service => {
    const currency = service.price.currency || service.currency
    let amount = service.price.monthlyRackRate || service.price.monthlyRate

    if (quoteConfig.rollUpDiscounts) {
      // If the user wants the discounts rolled up, use the monthly net charge.
      // However, if the service has a cross connect requested,
      // subtract the cross connect recurring charge from the monthly net charge since this charge is displayed separately.
      amount =
        hasFeatureFlag('cross_connect_mvp') && service.crossConnectRequested
          ? service.price.monthlyNetCharge - (service.price.crossConnectRecurringCharge || 0)
          : service.price.monthlyNetCharge
    }

    return formatCurrencyAmount(amount, currency)
  }

  const checkMonthlySubtotalVisibilityFor = (service, groupServiceCount = 0) => {
    // Nothing to show if the service is excluded from the quote.
    if (service.isExcludedFromQuote) return false

    // Nothing to show if the service is the only one in the group since it would be the same as the group's subtotal.
    if (groupServiceCount === 1) return false

    // At this point, always show the subtotal for services that have a cross connect requested since the cross connect has its own monthly rate.
    if (hasFeatureFlag('cross_connect_mvp') && service.crossConnectRequested) return true

    // If the service has a monthly rack rate that is greater than the monthly net charge,
    // which indicates the presence of a visible discount, show the subtotal based on the discount roll-up state.
    if (service.price.monthlyRackRate > service.price.monthlyNetCharge) return !quoteConfig.rollUpDiscounts
  }

  /**
   * The list of quote summaries, indexed by currency.
   */
  const perCurrencyQuoteSummaries = computed(() => {
    const currencyMap = new Map()

    for (const service of cloneDeep(enhancedServices.value)) {
      const currency = service.currency ?? 'USD'

      // Get the relevant currency group for this service
      let currencyGroup = currencyMap.get(currency)

      // Set a new currency group up if it doesn't exist yet
      if (!currencyGroup) {
        currencyGroup = { currency, serviceQuotes: [], monthlyTotal: 0, oneTimeFeesTotal: 0 }

        currencyMap.set(currency, currencyGroup)
      }

      // Calculate the Monthly subtotal and One-Time Fees subtotal
      let monthlyRate = getBaseDisplayMonthlyRate(service.price)
      let oneTimeFeesSubtotal = 0

      if (hasFeatureFlag('cross_connect_mvp') && service.crossConnectRequested) {
        monthlyRate += service.price.crossConnectRecurringCharge || 0
        oneTimeFeesSubtotal = service.isExcludedFromQuote ? 0 : service.price.crossConnectNonRecurringCharge || 0
      }

      const monthlySubtotal = service.connectionQuotes.reduce(
        (sum, connection) => {
          return connection.isExcludedFromQuote ? sum : sum + getBaseDisplayMonthlyRate(connection.price)
        },
        service.isExcludedFromQuote ? 0 : monthlyRate,
      )

      // Add the fetching status to the service
      const serviceQueryKey = service._basePricebookQueryOptions.queryKey

      service.price._isFetching =
        vueQueryPluginOptions.queryClient.getQueryState(serviceQueryKey)?.fetchStatus === 'fetching'
      // Determine the discounts to display for the service
      service.price.discountsToDisplay = getDiscountsToDisplay(service.price.discounts)
      // Store the monthly net charge
      service.price.monthlyNetCharge = monthlyRate

      // Add the fetching status to the service's connections and determine the discounts to display
      for (const connection of service.connectionQuotes) {
        const connectionQueryKey = connection._basePricebookQueryOptions.queryKey

        connection.price._isFetching =
          vueQueryPluginOptions.queryClient.getQueryState(connectionQueryKey)?.fetchStatus === 'fetching'
        connection.price.discountsToDisplay = getDiscountsToDisplay(connection.price.discounts)
        connection.price.monthlyNetCharge = getBaseDisplayMonthlyRate(connection.price)
      }

      // Add the service to the currency group
      currencyGroup.serviceQuotes.push({ ...service, monthlySubtotal, oneTimeFeesSubtotal })

      // Add the monthly subtotal to the monthly total
      currencyGroup.monthlyTotal += monthlySubtotal
      // One-time fees total
      currencyGroup.oneTimeFeesTotal += oneTimeFeesSubtotal
    }

    return Array.from(currencyMap.values()).sort((a, b) => a.currency.localeCompare(b.currency))
  })

  /**
   * Adds a new service to the service quotes.
   * @param {Object} service The service to add
   * @param {string} service.id The ID of the service - required so that private connections can be linked to the service
   * @param {string} service.serviceType The type of service (MEGAPORT/MCR2/MVE/LAG)
   * @param {string} service.name The name of the service
   * @param {number} service.location The ID of the location of the service
   * @param {Object} service.price The service's pricing details
   * @param {number} service.price.monthlyRate The monthly rate of the service
   * @param {number} [service.rateLimit] The rate limit of the service - only required for MEGAPORT/MCR2/LAG
   * @param {number} [service.portCount] The number of ports in the LAG - only required for LAG
   * @param {string} [service.size] The size of the service - only required for MVE
   * @param {string} [service.vendor] The vendor of the service - only required for MVE
   */
  const addService = service => {
    const serviceToAdd = cloneDeep(service)

    if (!serviceToAdd.connectionQuotes) {
      serviceToAdd.connectionQuotes = []
    }

    serviceQuotes.value.push(serviceToAdd)

    quoteIsDirty.value = true
  }

  /**
   * Adds a new connection to a service.
   * @param {Object} connection The connection to add
   * @param {string} connection.parentId The ID of the service to add the connection to
   * @param {string} [connection.id] The ID of the connection - only used internally and can be generated if not provided
   * @param {string} connection.name The name of the connection
   * @param {number} connection.speed The speed of the connection
   * @param {Object} connection.price The connection's pricing details
   * @param {number} connection.price.monthlyRate The monthly rate of the connection
   * @param {number} [connection.term] The term of the connection - only required for VXC
   * @param {number} [connection.bEndLocation] The location ID of the B end of the connection - only required for VXC
   * @param {string} [connection.connectType] The type of connection - only required for VXC
   * @param {string} [connection.ixType] The IX type of the connection - only required for IX
   */
  const addConnection = ({ parentId, ...connection }) => {
    const service = serviceQuotes.value.find(({ id }) => id === parentId)

    if (!service) {
      console.error(`Service with ID ${parentId} not found in store`)
      return
    }

    if (!connection.id) {
      connection.id = uuid()
    }

    service.connectionQuotes.push(connection)

    quoteIsDirty.value = true
  }

  const { enhancedTransitPorts, transitEnabledMarkets } = useTransitPorts()

  /**
   * Updates an existing service (Port/MCR/MVE), changing any attached connections as required but not updating them with any passed-in values.
   * This is to ensure that currency is kept in sync, and that transit VXCs stay within the same metro.
   * @param {Object} service The service to update
   */
  const updateServiceAndConnections = service => {
    // Update the service first
    updateServiceOnly(service)

    // Now iterate over the list of connections, updating them as required.
    const enhancedService = enhancedServices.value.find(({ id }) => id === service.id)

    if (!enhancedService) {
      console.error(`Service with ID ${enhancedService.id} not found in store`)
      return
    }

    for (const connection of enhancedService.connectionQuotes) {
      const connectType = connection.connectionType?.split('|')[0]

      if (connection.currency !== enhancedService.currency || connectType === 'TRANSIT') {
        // Deep clone the connection to avoid accidentally modifying the original
        const updatedConnection = cloneDeep(connection)

        // Set the connection's parent ID to the service's ID
        updatedConnection.parentId = enhancedService.id

        // Ensure the currency is kept in sync
        updatedConnection.currency = enhancedService.currency

        // If the connection is a transit VXC, ensure it stays within the same market. If not possible, delete it.
        if (connectType === 'TRANSIT') {
          const serviceLocation = enhancedService._location
          const connectionLocation = enhancedLocations.value.find(({ id }) => id === connection.bEndLocation)

          const marketMismatch = serviceLocation.market !== connectionLocation.market

          // If the location of the service has changed and is now in a different market,
          // try to find a transit port in the new market to connect to. If not possible, delete the connection.
          if (marketMismatch) {
            const isMve = enhancedService.serviceType === PRODUCT_TYPE_MVE
            const marketIsTransitEnabled = transitEnabledMarkets.value?.includes(serviceLocation.market)

            // If the new location is in a transit-enabled market, find a transit port in that market to connect to.
            // NOTE: For MVEs, all markets are transit-enabled.
            // NOTE: For other service types, only certain markets are transit-enabled.
            if (isMve || marketIsTransitEnabled) {
              const transitPort = enhancedTransitPorts.value?.find(({ market }) => market === serviceLocation.market)

              if (transitPort) {
                updatedConnection.bEndLocation = transitPort.id
              } else {
                deleteConnection(updatedConnection.id)

                // Notify the user of the deletion
                window.mpApp.$notify.warning({
                  title: i18n.t('connections.deleted'),
                  message: i18n.t('connections.transit-vxc-deleted-no-target', {
                    connectionName: updatedConnection.name,
                  }),
                  showClose: false,
                })
              }
            } else {
              deleteConnection(updatedConnection.id)

              // Notify the user of the deletion
              window.mpApp.$notify.warning({
                title: i18n.t('connections.deleted'),
                message: i18n.t('connections.transit-vxc-deleted-market-not-enabled', {
                  connectionName: updatedConnection.name,
                }),
                showClose: false,
              })
            }
          }
        }

        // Update the connection in the store
        updateConnection(updatedConnection)
      }
    }
  }

  /**
   * Updates an existing service (Port/MCR/MVE), replacing the entire object with the new one,
   * but maintaining the connection quotes as they were. The service argument must have an ID.
   * @param {Object} service The service to update
   */
  const updateServiceOnly = service => {
    const index = serviceQuotes.value.findIndex(({ id }) => id === service.id)

    if (index === -1) {
      console.error(`Service with ID ${service.id} not found in store`)
      return
    }

    serviceQuotes.value.splice(index, 1, { ...service, connectionQuotes: serviceQuotes.value[index].connectionQuotes })

    quoteIsDirty.value = true
  }

  const updateServicePricing = (serviceId, newPricing) => {
    // Nothing to update if there are no services in the store
    if (serviceQuotes.value.length === 0) return

    const service = serviceQuotes.value.find(({ id }) => id === serviceId)

    if (!service) {
      console.error(`Service with ID ${serviceId} not found in store`)
      return
    }

    const monthlyRate =
      service.serviceType === PRODUCT_TYPE_LAG
        ? newPricing.monthlyRate * (service.portCount ?? 1)
        : newPricing.monthlyRate

    service.price.monthlyRate = monthlyRate
    service.price.currency = newPricing.currency
  }

  /**
   * Updates an existing connection, replacing the entire object with the new one minus the parent ID.
   * @param {Object} connection The connection to update (must contain an ID and a parent ID)
   */
  const updateConnection = ({ parentId, ...connection }) => {
    const service = serviceQuotes.value.find(({ id }) => id === parentId)

    if (!service) {
      console.error(`Service with ID ${parentId} not found in store`)
      return
    }

    const connectionIndex = service.connectionQuotes.findIndex(({ id }) => id === connection.id)

    if (connectionIndex === -1) {
      console.error(`Connection with ID ${connection.id} not found in store`)
      return
    }

    service.connectionQuotes.splice(connectionIndex, 1, connection)

    quoteIsDirty.value = true
  }

  const updateConnectionPricing = (parentId, connectionId, newPricing) => {
    // Nothing to update if there are no services in the store
    if (serviceQuotes.value.length === 0) return

    const service = serviceQuotes.value.find(({ id }) => id === parentId)

    if (!service) {
      console.error(`Service with ID ${parentId} not found in store`)
      return
    }

    const connection = service.connectionQuotes.find(({ id }) => id === connectionId)

    if (!connection) {
      console.error(`Connection with ID ${connectionId} not found in store`)
      return
    }

    connection.price.monthlyRackRate = newPricing.monthlyRackRate
    connection.price.monthlyRate = newPricing.monthlyRate
    connection.price.discounts = newPricing.discounts
    connection.price.currency = newPricing.currency
  }

  /**
   * Deletes a service from the service quotes.
   */
  const deleteService = serviceId => {
    const index = serviceQuotes.value.findIndex(({ id }) => id === serviceId)

    if (index !== -1) {
      const deletedService = cloneDeep(serviceQuotes.value[index])

      serviceQuotes.value.splice(index, 1)

      // Now we need to go through all the other services and see if there were any private connections that rely on this service.
      // This will be true for private connections where the service type and the B end of the connection match those of the deleted service.
      const candidates = []
      const connectType = `PRIVATE|${deletedService.serviceType}`

      for (const service of serviceQuotes.value) {
        const matchingConnections = service.connectionQuotes.filter(
          connection => connection.connectType === connectType && connection.bEndLocation === deletedService.location,
        )

        candidates.push(...matchingConnections)
      }

      // Now for each candidate we check if there still is a service that supports the connection.
      for (const candidate of candidates) {
        let connectionIsSupported = false

        for (const service of serviceQuotes.value) {
          const sameServiceType = service.serviceType === deletedService.serviceType
          const sameLocation = service.location === deletedService.location
          const supportsRateLimit = service.rateLimit >= candidate.speed

          if (sameServiceType && sameLocation && supportsRateLimit) {
            connectionIsSupported = true
            break
          }
        }

        // If no service supports the connection anymore, delete it.
        if (!connectionIsSupported) {
          deleteConnection(candidate.id)
        }
      }

      quoteIsDirty.value = true
    }

    console.error(`Service with ID ${serviceId} not found in store`)
  }

  /**
   * Deletes a connection from the service quotes.
   * @param {string} connectionId The ID of the connection to delete
   */
  const deleteConnection = connectionId => {
    for (const service of serviceQuotes.value) {
      const index = service.connectionQuotes.findIndex(({ id }) => id === connectionId)

      if (index !== -1) {
        service.connectionQuotes.splice(index, 1)

        quoteIsDirty.value = true

        return
      }
    }

    console.error(`Connection with ID ${connectionId} not found in store`)
  }

  return {
    quoteConfig,
    serviceQuotes,
    quoteIsDirty,
    enhancedServices,
    perCurrencyQuoteSummaries,
    isForeignQuote,
    isForeignQuoteNotOwnedByMegaport,
    isForSelf,
    isForExistingCompany,
    isForNewCompany,
    isRegisteredQuote,
    pricingResults,
    quoteTargetAccountType,
    canReceiveAccountTypeDiscounts,
    formattedCreateDate,
    clearConfigurations,
    populateQuoteFromApi,
    getMonthlyPriceToDisplay,
    checkMonthlySubtotalVisibilityFor,
    addService,
    addConnection,
    updateConnection,
    updateServiceAndConnections,
    saveQuote,
    deleteService,
    deleteConnection,
    registerDeal,
    updateServicePricing,
    updateConnectionPricing,
  }
})

// Enable Hot Module Replacement
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useConfiguredServicesStore, import.meta.hot))
}
